class SignupLp extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    if (!this.form) return;

    this.form.addEventListener("submit", (e) => {
      e.preventDefault();
      this.submitForm();
    });

    this.initSmsDisclaimer();
  }

  initSmsDisclaimer() {
    const phoneInput = this.form.querySelector('input[name="phone"]');
    const disclaimer = this.querySelector("[data-sms-disclaimer]");
    if (!phoneInput || !disclaimer) return;
    if (phoneInput.hasAttribute("required")) return;

    const smsCheckbox = disclaimer.querySelector('input[name="sms_consent"]');

    phoneInput.addEventListener("input", () => {
      const hasValue = phoneInput.value.trim().length > 0;
      disclaimer.classList.toggle("hidden", !hasValue);
      if (!hasValue && smsCheckbox) {
        smsCheckbox.checked = false;
      }
    });
  }

  async submitForm() {
    const error = this.querySelector(".error");
    const successContainer = this.querySelector("[data-signup-lp-success]");
    const submit = this.form.querySelector('[type="submit"]');

    const emailInput = this.form.querySelector('input[name="email"]');
    const zipInput = this.form.querySelector('input[name="zip"]');
    const nameInput = this.form.querySelector('input[name="name"]');
    const phoneInput = this.form.querySelector('input[name="phone"]');
    const smsConsentInput = this.form.querySelector('input[name="sms_consent"]');

    const listId = this.dataset.listId;
    const countryIso = (
      this.dataset.countryIso ||
      window.Bero?.currentCountryCode ||
      window.Shopify?.country ||
      ""
    ).toUpperCase();
    const countryName = this.dataset.countryName || "";
    const customSource = this.dataset.customSource || "Signup LP";

    const region = window.Shopify?.customerPrivacy?.getRegion?.() || "";
    // Prioritize market-based countryIso over IP-based region
    const isUK = countryIso === "GB" || (!countryIso && region.includes("GB"));
    const isUS = !isUK && (countryIso === "US" || (!countryIso && region.includes("US")));

    if (error) {
      error.textContent = "";
      error.classList.add("hidden");
    }

    const submitText = submit.innerHTML;
    submit.innerHTML = "Submitting...";
    submit.disabled = true;

    try {
      const email = (emailInput?.value || "").trim();
      const postalCode = (zipInput?.value || "").trim();
      const name = (nameInput?.value || "").trim();
      const phone = (phoneInput?.value || "").trim();

      const emailRequired = emailInput?.hasAttribute("required");
      const zipRequired = zipInput?.hasAttribute("required");
      const nameRequired = nameInput?.hasAttribute("required");
      const phoneRequired = phoneInput?.hasAttribute("required");

      if (emailRequired && (!email || !email.includes("@"))) {
        throw new Error("Please enter a valid email address.");
      }

      if (email && !email.includes("@")) {
        throw new Error("Please enter a valid email address.");
      }

      if (nameRequired && !name) {
        throw new Error("Please enter your name.");
      }

      if (phoneRequired && !phone) {
        throw new Error("Please enter your phone number.");
      }

      if (phone) {
        const phoneDigits = phone.replace(/\D/g, "");
        if (phoneDigits.length < 7 || phoneDigits.length > 15) {
          throw new Error("Please enter a valid phone number.");
        }
      }

      if (smsConsentInput && phone && !smsConsentInput.checked) {
        throw new Error("Please agree to the SMS terms to continue.");
      }

      if (zipRequired && !postalCode) {
        throw new Error(
          isUK ? "Please enter your postcode." : "Please enter your zip code."
        );
      }

      if (postalCode) {
        if (isUS && !/^\d{5}$/.test(postalCode)) {
          throw new Error("Zip code must be exactly 5 digits.");
        }

        if (isUK) {
          const ukRegex = /^[A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9][A-Z]{2}$/i;
          if (!ukRegex.test(postalCode)) {
            throw new Error("Please enter a valid postcode.");
          }
        }

        if (!isUS && !isUK && (postalCode.length < 3 || postalCode.length > 10)) {
          throw new Error("Please enter a valid postal code.");
        }
      }

      const normalizeRegion = (value, prefix) => {
        if (!value || !value.includes(prefix)) return "";
        return value.split(prefix)[1]?.replace(/^[-_]/, "") || "";
      };

      const location = { zip: postalCode };
      if (isUS) {
        location.country = "United States";
        location.region = normalizeRegion(region, "US");
      } else if (isUK) {
        location.country = "United Kingdom";
        location.region = normalizeRegion(region, "GB");
      } else if (countryName) {
        location.country = countryName;
      } else if (countryIso) {
        location.country = countryIso;
      }

      const signUpSource = isUS ? "US" : isUK ? "UK" : "INT";
      const updateableSource = countryIso
        ? countryIso === "GB"
          ? "UK"
          : countryIso
        : signUpSource;

      const properties = {
        ad_personalization: "granted",
        ad_user_data: "granted",
        "Accepts Marketing": true,
        sign_up_source: signUpSource,
        updateable_source: updateableSource,
      };

      const locale = isUS ? "en-US" : isUK ? "en-GB" : "";

      const subscriptions = {
        email: { marketing: { consent: "SUBSCRIBED" } },
      };

      if (phone) {
        subscriptions.sms = { marketing: { consent: "SUBSCRIBED" } };
      }

      const profileAttributes = {
        email,
        location,
        ...(locale && { locale }),
        subscriptions,
        properties,
      };

      if (name) profileAttributes.first_name = name;
      if (phone) {
        const inputTelIntl = this.querySelector("input-tel-intl");
        profileAttributes.phone_number = inputTelIntl
          ? inputTelIntl.format(phone)
          : "+" + phone.replace(/\D/g, "");
      }

      const payload = {
        data: {
          type: "subscription",
          attributes: {
            profile: {
              data: {
                type: "profile",
                attributes: profileAttributes,
              },
            },
            custom_source: customSource,
          },
          ...(listId && {
            relationships: {
              list: { data: { type: "list", id: listId } },
            },
          }),
        },
      };

      const resp = await fetch(
        "https://a.klaviyo.com/client/subscriptions/?company_id=UYwKnY",
        {
          method: "POST",
          headers: {
            Accept: "application/vnd.api+json",
            "Content-Type": "application/vnd.api+json",
            revision: "2025-01-15",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(
          errorData?.errors?.[0]?.detail ||
            "Something went wrong. Please try again."
        );
      }

      this.form.reset();
      if (successContainer) successContainer.classList.remove("hidden");
    } catch (e) {
      if (error) {
        error.textContent =
          e.message || "Oops, there was an error. Please try again.";
        error.classList.remove("hidden");
      }
    } finally {
      submit.innerHTML = submitText;
      submit.disabled = false;
    }
  }
}

customElements.define("signup-lp", SignupLp);
